<template>
  <div :class="wrapperClasses">
    <div class="listing">
      <b-field class="search-controls">
        <input v-model="search" size="60" placeholder="User ID" v-on:keyup.enter="filterByUid" class="search" />
        <span v-on:click="filterByUid" class="button-wrapper">
          <b-radio-button native-value="uid" type="is-light">
            <b-icon icon="magnify" />
          </b-radio-button>
        </span>
      </b-field>
      <b-table :data="rows" :row-class="(row, index) => assignRowClasses(row, index)">
        <template slot-scope="props">
          <b-table-column class="key" field="key" label="Category">{{props.row.label}}</b-table-column>
          <b-table-column field="subject" label="Artists">{{props.row.numSubmissions}}</b-table-column>
          <b-table-column field="works" label="Works">{{props.row.numWorks}}</b-table-column>
          <b-table-column field="slides" label="Slides">
            <span class="num">{{props.row.numSlides}}</span>
          </b-table-column>
          <b-table-column field="_id" label="Start" class="play-column">
            <span v-if="showRestartLink(props.row)" class="play restart" v-on:click="loadData(props.row, 0)"
              title="Restart from the first slide">
              <b-icon icon="play-circle" size="is-large" />
            </span>
            <span class="start-user-control" v-if="props.row.key === filter || props.row.key === 'valid'">
              <label :for="[props.row.key,'uid'].join('-')">Start from user</label>
              <input v-model="startUid" size="60" type="number" placeholder="User ID" title="Start from User ID"
                class="start-user" :id="[props.row.key,'uid'].join('-')" />
            </span>
            <span v-if="props.row.numSubmissions > 0" class="play" v-on:click="loadData(props.row, startIndex, true)"
              :title="restartTooltip(props.row.key)">
              <b-icon icon="play-circle-outline" size="is-large" />
            </span>
          </b-table-column>
        </template>
      </b-table>

      <b-switch type="is-success" v-model="showByMedia" class="toggle-show-media" size="is-small" rounded>{{ toggleShowMediaLabel }}</b-switch>
    </div>
    <b-loading :is-full-page="true" :active.sync="loading" :can-cancel="true"></b-loading>
    <review-details v-if="showDetail" :data="currentData" :filter="filter" :start="startIndex" :mayMark="isEditor" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { listSlides, getOverview } from "../api/methods";
import filters from "../mixins/filters";
import { bus } from "../main";
import { mapMediaItem } from "../api/mappers";
import { isNumeric, notEmptyString } from "../api/helpers";
import { addBodyClass, removeBodyClass } from "../api/dom";
import ReviewDetails from "./ReviewDetails";

export default {
  name: "ReviewList",
  components: {
    ReviewDetails
  },
  mixins: [filters],
  data() {
    return {
      rows: [],
      labels: {
        user: "Candidate",
        valid: "Submitted and eligible",
        withImages: "with images only",
        withVideoAudio: "with video and/or audio",
        //withVideo: "with video",
        //withAudio: "with audio",
        //returnTo: "Return to",
        shortlisted: "Return to",
        selected: "Shortlisted works",
        numShortlisted: 0,
        numSelected: 0
      },
      currentData: {
        key: "",
        slides: []
      },
      filter: "",
      showDetail: false,
      startIndex: 0,
      startUid: 0,
      search: "",
      candidateData: {
        numWorks: 0,
        numSlides: 0,
        withImages: 0,
        withVideo: 0,
        numWorksWithImages: 0,
        numWorksWithVideo: 0,
        numWorksWithAudio: 0
      },
      loading: true,
      showByMedia: false
    };
  },
  computed: {
    ...mapGetters(["settings", "options", "user"]),
    wrapperClasses() {
      let cls = ["submissions"];
      if (this.showDetail) {
        cls.push("show-detail");
      }
      return cls;
    },
    slideNum() {
      return this.startIndex + 1;
    },
    isEditor() {
      return ["admin","selector"].includes(this.user.role);
    },
    toggleShowMediaLabel() {
      return this.showByMedia ? "Hide media categories" : "Show media categories";
    }
  },
  created() {
    const showByM = this.$ls.get("show-media-categories");
    if (showByM === true) {
      this.showByMedia = true;
    }
    this.loadOverview();
    bus.$on("slide-update", data => {
      if (data instanceof Object) {
        this.updateSlideData(data);
        let msg = "";
        const { key, newVal} = data;
        switch (key) {
          case 'shortlisted':
            msg = newVal? `Return to this submission later` : `Do not return to this submission later`;
            break;
          case 'selected':
            msg = ['Work', newVal? 'selected' : 'deselected'].join(' ');
            break;
        }
        if (notEmptyString(msg)) {
          this.toast(msg, 2000);
        }
      }
    });
    bus.$on("refresh", () => {
      const { path } = this.$route;
      if (path.indexOf("review") >= 0) {
        this.loadOverview();
      }
    });
  },
  mounted() {
    this.checkStartParams();
  },
  watch: {
    showDetail(cv, pv) {
      if (cv !== pv) {
        if (cv) {
          addBodyClass("fullscreen");
        } else {
          removeBodyClass("fullscreen");
        }
      }
    },
    filter(cv, pv) {
      if (cv !== pv) {
        this.startIndex = 0;
        this.$ls.set("review-filter", cv, 7 * 24 * 60 * 60 * 1000);
      }
    },
    startIndex(cv, pv) {
      if (cv !== pv) {
        this.$ls.set("review-start-index", cv, 7 * 24 * 60 * 60 * 1000);
      }
    },
    showByMedia(cv, pv) {
      if (cv !== pv) {
        this.loadOverview();
        this.$ls.set("show-media-categories", cv, 7 * 24 * 60 * 60 * 1000);
      }
    }
  },
  methods: {
    loadOverview() {
      this.loading = true;
      getOverview(this.isEditor).then(data => {
        if (data.valid) {
          this.updateRows(data);
          setTimeout(this.loadFromPath, 250);
          this.loading = false;
        }
      });
    },
    updateRows(data) {
      const rows = data.items
        .filter(this.filterRow)
        .map(this.preMapRow);
      const shortRow = rows.find(r => r.key === "shortlisted");
      if (shortRow) {
        this.numShortlisted = shortRow.numSubmissions;
      }
      const selRow = rows.find(r => r.key === "selected");
      if (selRow) {
        this.numSelected = selRow.numSubmissions;
      }
      this.rows = rows.filter(this.filterRows).map(this.postMapRow);
    },
    filterRow(row) {
      let valid = false;
      const excludeFromRemote = ['valid','shortlisted','selected', 'withImages'];
      if (row instanceof Object) {
        valid = Object.keys(this.labels).includes(row.key);
        if (valid && !this.isEditor) {
          valid = excludeFromRemote.includes(row.key) === false;
        }
      }
      return valid;
    },
    preMapRow(row) {
      row.label = this.labels[row.key];
      row.mayMail = false;
      switch (row.key) {
        case "valid":
        case "shortlisted":
        case "selected":
          row.mayMail = row.numSubmissions > 0;
          switch (row.key) {
            case "valid":
              row.mail = {
                key: "not_shortlisted",
                label: "Not shortlisted",
                proceed: false,
                num: 0
              };
              break;
            case "shortlisted":
              row.mail = {
                key: "shortlisted",
                label: "Shortlisted",
                proceed: true,
                num: 0
              };
              row.mailTypes = [];
              break;
            case "selected":
              row.mail = {
                key: "not_selected",
                label: "Shortlisted, but not selected",
                proceed: false,
                num: 0
              };
              break;
          }
          break;
      }
      return row;
    },
    postMapRow(row) {
      switch (row.key) {
        case "valid":
          row.mail.num = row.numSubmissions - this.numShortlisted;
          break;
        case "shortlisted":
          row.mail.num = this.numShortlisted;
          break;
        case "selected":
          row.mail.num = this.numShortlisted - this.numSelected;
          break;
      }
      return row;
    },
    filterRows(row) {
      let valid = false;
      if (row instanceof Object) {
        const { key } = row;
        valid = notEmptyString(key, 3);
        if (valid && !this.showByMedia) {
          valid = key.startsWith('with') === false;
        }
      }
      return valid;
    },
    hasPreview(filter) {
      let valid = false;
      const keys = Object.keys(this.totals);
      if (keys.indexOf(filter) >= 0) {
        if (this.totals[filter].preview instanceof Object) {
          valid = this.totals[filter].preview.numSubmissions > 0;
        }
      }
      return valid;
    },
    loadData(row, targetIndex = 0, searchUid = false) {
      const { stage, media } = row;
      if (!searchUid) {
        this.startUid = 0;
      }
      this.filter = row.key;
      let useCurrent = false;
      if (this.currentData.key) {
        if (this.currentData.key === this.filter) {
          if (this.currentData.slides) {
            useCurrent = this.currentData.slides.length > 0;
          }
        }
      }
      if (isNumeric(targetIndex)) {
        this.startIndex = parseInt(targetIndex);
      }
      let data = null;
      const cKey = ["slideshow", stage, media].join("-");
      if (useCurrent) {
        data = this.currentData;
      } else {
        data = this.$ls.get(cKey);
      }
      this.loading = true;
      if (data instanceof Object) {
        this.assignSlideShow(data, null, targetIndex);
      } else {
        listSlides(stage, media).then(data =>
          this.assignSlideShow(data, cKey, targetIndex)
        );
      }
    },
    assignSlideShow(data, cKey, targetIndex, uid) {
      if (isNumeric(uid)) {
        uid = parseInt(uid);
      } else {
        uid = 0;
      }
      setTimeout(() => {
        this.loading = false;
      }, 250);
      if (data.numSubmissions && data.slides instanceof Array) {
        if (this.startUid > 0) {
          const uidIndex = data.slides.findIndex(sl => {
            let vl = false;
            if (sl.submission) {
              vl = parseInt(sl.submission.uid) >= parseInt(this.startUid);
            }
            return vl;
          });
          if (uidIndex >= 0) {
            targetIndex = uidIndex;
            this.startIndex = uidIndex;
          }
        }
        this.currentData = { key: this.filter, ...data };
        let slideNum = 0;
        this.currentData.slides = data.slides.map((slide, index) => {
          if (["intro", "end"].indexOf(slide.type) < 0) {
            slideNum++;
          }
          if (slide.work) {
            if (slide.indices instanceof Array) {
              if (slide.indices.length === 3) {
                slide.work.index = slide.indices[1];
              }
            }
          }
          return { index, slideNum, ...mapMediaItem(slide) };
        });

        this.showDetail = true;
        const endSlide = this.currentData.slides.find(s => s.type === "end");
        if (!endSlide) {
          const obj = {
            index: this.currentData.slides.length,
            indices: [0, 0, 0],
            numWorks: 0,
            numSlides: 0,
            numMediaItems: "",
            _id: "",
            filename: "",
            sourceFilename: "end",
            mime: "text/plain",
            source: "",
            size: "",
            type: "end",
            title: "The End",
            work: {},
            submission: {},
            src: ""
          };
          this.currentData.slides.push(obj);
        }
        if (isNumeric(targetIndex)) {
          const tgIndex = parseInt(targetIndex);
          if (tgIndex > 0 && tgIndex < this.currentData.slides.length) {
            this.startIndex = tgIndex;
          }
        }
        if (uid < 1 && notEmptyString(cKey, 5)) {
          if (data.slides.length < 512) {
            this.$ls.set(cKey, data, 30 * 1000);
          }
        }
      }
    },
    loadFromPath() {
      const parts = this.$route.path.substring(1).split("/");
      if (parts.length > 1) {
        const [_, filter, index] = parts;
        const row = this.rows.find(r => r.key === filter);
        if (row instanceof Object) {
          if (row.stage && notEmptyString(_)) {
            let targetIndex = 0;
            if (isNumeric(index)) {
              targetIndex = parseInt(index);
            }
            this.loadData(row, targetIndex);
          }
        }
      }
    },
    assignRowClasses(row, index) {
      let cls = [];
      if (index % 2 === 0) {
        cls.push("even");
      }
      return cls.join(" ");
    },
    updateSlideData(data) {
      if (this.currentData.slides instanceof Array && data instanceof Object) {
        if (this.currentData.slides.length > 0) {
          let subSlides = this.currentData.slides.filter(
            sl => sl.submission._id === data.subId
          );
          for (let slide of subSlides) {
            switch (data.key) {
              case "selected":
                if (slide.work._id === data.workId) {
                  slide.work.selected = data.newVal;
                }
                break;
              case "shortlisted":
              case "returnTo":
              case "longRead":
                slide.submission[data.key] = data.newVal;
                break;
            }
          }
          const hasSelectedWork = subSlides.some(sl => sl.work.selected);
          subSlides = subSlides.map(sl => {
            sl.submission.hasSelectedWork = hasSelectedWork;
          });
        }
      }
    },
    restartTooltip(key) {
      this.checkStartParams();
      let tt = "";
      if (parseInt(this.startUid) > 0) {
        tt = "Start from User ID: " + this.startUid;
      } else if (this.startIndex > 0 && this.filter === key) {
        tt = "Resume from the last slide viewed";
      } else {
        tt = "Start from the first slide";
      }
      return tt;
    },
    checkStartParams() {
      let storedStartIndex = this.$ls.get("review-start-index");
      if (isNumeric(storedStartIndex)) {
        storedStartIndex = parseInt(storedStartIndex);
        if (storedStartIndex > 0) {
          this.startIndex = storedStartIndex;
        }
      }
      const storedFilter = this.$ls.get("review-filter");
      if (notEmptyString(storedFilter, 3)) {
        this.filter = storedFilter;
      }
    },
    showRestartLink(row) {
      let vl = false;
      if (row instanceof Object && notEmptyString(this.filter, 3)) {
        if (row.numSubmissions) {
          vl =
            row.numSubmissions > 0 &&
            this.startIndex > 1 &&
            this.filter === row.key;
        }
      }
      return vl;
    },
    close(index) {
      this.showDetail = false;
      if (isNumeric(index)) {
        this.startIndex = parseInt(index);
        if (this.startIndex > 1) {
          if (
            this.currentData.key === this.filter &&
            this.currentData.slides instanceof Array
          ) {
            if (this.currentData.slides.length > this.startIndex) {
              const sl = this.currentData.slides[this.startIndex];
              if (sl instanceof Object) {
                if (sl.submission) {
                  this.startUid = parseInt(sl.submission.uid);
                }
              }
            }
          }
        } else {
          this.startUid = 0;
        }
        this.loadOverview();
      }
      const section = this.$route.path
        .substring(1)
        .split("/")
        .shift();
      const newPath = "/" + section;
      if (this.$route.path !== newPath) {
        this.$router.push(newPath);
      }
    },
    filterByUid() {
      const inval = this.search.toString().trim();
      if (inval.length > 0 && isNumeric(inval)) {
        const uid = parseInt(inval);
        if (uid > 0) {
          listSlides("user", "all", uid).then(data => {
            this.assignSlideShow(data, null, 0, uid);
            let ds = { key: "user", stage: "valid", media: "all" };
            Object.entries(et => {
              const [k, v] = et;
              switch (k) {
                case "slides":
                case "valid":
                  break;
                default:
                  ds[k] = v;
                  break;
              }
            });
            this.candidateData = ds;
            this.filter = ds.key;
          });
        }
      }
    },
    toast(message = "", duration = 3000) {
      this.$buefy.toast.open({
        duration,
        message,
        position: "is-bottom",
        type: "is-success"
      });
    },
  }
};
</script>
